import type { IAgencyProduct } from '~/types/agencies';

export type EtaiementRentalPriceUnit = 'm2' | 'linearMeter' | 'ton' | 'flat' | 'unit';

export interface IEtaiementRentalRow {
  productType?: string;
  productId?: string;
  duration?: number | string;
  priceUnit?: EtaiementRentalPriceUnit | '';
  quantity?: number | null;
  unitPrice?: number | null;
  lineTotal?: number | null;
}

export const RENTAL_PRICE_UNIT_OPTIONS = [
  { label: 'm²', value: 'm2' },
  { label: 'm linéaire', value: 'linearMeter' },
  { label: 'tonne', value: 'ton' },
  { label: 'Forfait', value: 'flat' },
  { label: 'unité', value: 'unit' },
] as const;

const isValidObjectIdString = (value: unknown): value is string => (
  typeof value === 'string' && /^[a-f\d]{24}$/i.test(value)
);

export const isRentalRowPersistable = (
  row: IEtaiementRentalRow | null | undefined,
): boolean => {
  if (!isValidObjectIdString(row?.productId ? String(row.productId) : '')) {
    return false;
  }
  const duration = row?.duration;
  return duration !== '' && duration !== null && duration !== undefined;
};

export const getRentalUnitPrice = (
  agencyProduct: IAgencyProduct | undefined,
  priceUnit: string | undefined,
): number => {
  if (!agencyProduct || !priceUnit) return 0;
  switch (priceUnit) {
    case 'm2':
      return Number(agencyProduct.pricePerM2) || 0;
    case 'linearMeter':
      return Number(agencyProduct.pricePerLinearMeter) || 0;
    case 'ton':
      return Number(agencyProduct.pricePerTon) || 0;
    case 'flat':
      return Number(agencyProduct.flatPrice) || 0;
    case 'unit':
      return Number(agencyProduct.pricePerUnit) || 0;
    default:
      return 0;
  }
};

export const getRentalDurationMultiplier = (
  duration: number | string | null | undefined,
): number => {
  const dur = Number(duration);
  if (!Number.isFinite(dur)) return 0;
  if (dur === -1) return 1;
  return dur > 0 ? dur : 0;
};

/** Missing quantity (legacy rows) counts as 1. Explicit 0 yields 0. */
export const getRentalQuantityMultiplier = (
  quantity: number | string | null | undefined,
): number => {
  if (quantity === '' || quantity == null) return 1;
  const qty = Number(quantity);
  if (!Number.isFinite(qty) || qty <= 0) return 0;
  return qty;
};

/** Form Total column: unitPrice × quantity × duration (no discount). */
export const computeRentalGrossLineTotal = (
  unitPrice: number,
  duration: number | string | null | undefined,
  quantity?: number | string | null,
): number => {
  const unit = Number(unitPrice) || 0;
  if (unit <= 0) return 0;
  const durationMultiplier = getRentalDurationMultiplier(duration);
  if (durationMultiplier <= 0) return 0;
  const quantityMultiplier = getRentalQuantityMultiplier(quantity);
  if (quantityMultiplier <= 0) return 0;
  return Math.round(unit * quantityMultiplier * durationMultiplier * 100) / 100;
};

export const computeRentalLineTotal = (
  unitPrice: number,
  duration: number | string | null | undefined,
  discount: number,
  quantity?: number | string | null,
): number => {
  const unit = Number(unitPrice) || 0;
  if (unit <= 0) return 0;
  const durationMultiplier = getRentalDurationMultiplier(duration);
  if (durationMultiplier <= 0) return 0;
  const quantityMultiplier = getRentalQuantityMultiplier(quantity);
  if (quantityMultiplier <= 0) return 0;
  const discountedUnit = unit * (1 - (Number(discount) || 0));
  return Math.round(discountedUnit * quantityMultiplier * durationMultiplier * 100) / 100;
};

export const emptyRentalRow = (): IEtaiementRentalRow => ({
  productType: '',
  productId: '',
  duration: '',
  priceUnit: '',
  quantity: 1,
  unitPrice: null,
  lineTotal: null,
});

export const sanitizeRentalsForPersistence = (
  rows: IEtaiementRentalRow[] | null | undefined,
): IEtaiementRentalRow[] => {
  if (!rows?.length) return [];
  return rows
    .filter(isRentalRowPersistable)
    .map((row) => ({
      productType: row.productType?.trim() || '',
      productId: String(row.productId),
      duration: row.duration === '' ? '' : Number(row.duration),
      priceUnit: row.priceUnit || '',
      quantity: row.quantity == null ? null : Number(row.quantity),
      unitPrice: row.unitPrice ?? null,
      lineTotal: row.lineTotal ?? null,
    }));
};

export const isRentalSummaryStep = (value: string | null | undefined): boolean => (
  value === 'rental' || (typeof value === 'string' && value.startsWith('rentals.'))
);

export const isPrestationSummaryStep = (value: string | null | undefined): boolean => (
  typeof value === 'string' && value.startsWith('prestations.')
);
